/**
 * Remittances Service
 * Aggregator remittances (reversements), their tracking and the reminders.
 * Reserved to OGF structures (ADMIN, CASH_TRACKING, GLOBAL_CASH_TRACKING, AGENCY_HEAD).
 */

import apiClient from './api';

// ── Types ────────────────────────────────────────────────────────────

export type RemittanceAggregator = 'FICO_PAY' | 'MOOV_MONEY' | 'KONOOM' | 'AIRTEL_MONEY';
export type RemittanceChannel =
  | 'ORANGE_MONEY'
  | 'MTN_MOBILE_MONEY'
  | 'FICO_COUNTER'
  | 'MOOV_MONEY'
  | 'KONOOM'
  | 'AIRTEL_MONEY';
export type RemittanceMethod = 'CASH' | 'BANK_TRANSFER' | 'CHEQUE' | 'ELECTRONIC_PAYMENT';
export type RemittanceStatus = 'ACTIVE' | 'CANCELLED';
export type RemittanceTrackingStatus =
  | 'UP_TO_DATE'
  | 'TO_REMIT'
  | 'LATE'
  | 'CRITICAL'
  | 'OVER_REMITTED';
export type RemittanceGranularity = 'day' | 'week' | 'month';

export const REMITTANCE_AGGREGATORS: RemittanceAggregator[] = [
  'FICO_PAY',
  'MOOV_MONEY',
  'KONOOM',
  'AIRTEL_MONEY',
];
export const REMITTANCE_METHODS: RemittanceMethod[] = [
  'CASH',
  'BANK_TRANSFER',
  'CHEQUE',
  'ELECTRONIC_PAYMENT',
];
export const REMITTANCE_TRACKING_STATUSES: RemittanceTrackingStatus[] = [
  'CRITICAL',
  'LATE',
  'TO_REMIT',
  'OVER_REMITTED',
  'UP_TO_DATE',
];

export interface Remittance {
  id: number;
  reference: string;
  aggregator: RemittanceAggregator;
  remittance_date: string;
  amount: number;
  method: RemittanceMethod;
  observation?: string | null;
  has_justification: boolean;
  justification_file_name?: string | null;
  justification_mime_type?: string | null;
  status: RemittanceStatus;
  agency_id: number;
  agency_name?: string | null;
  structure_id: number;
  created_by_id: number;
  created_by_name?: string | null;
  created_at: string;
  cancelled_at?: string | null;
  cancelled_by_id?: number | null;
  cancelled_by_name?: string | null;
  cancellation_reason?: string | null;
  can_cancel: boolean;
}

export interface RemittanceListResponse {
  items: Remittance[];
  total: number;
  page: number;
  size: number;
  pages: number;
  currency_code: string;
  summary: {
    total_amount: number;
    count: number;
    cancelled_count: number;
    by_aggregator: { aggregator: RemittanceAggregator; amount: number; count: number }[];
  };
}

export interface RemittanceFilters {
  search?: string;
  date_from?: string;
  date_to?: string;
  agency_ids?: number[];
  aggregators?: RemittanceAggregator[];
  methods?: RemittanceMethod[];
  statuses?: RemittanceStatus[];
  amount_min?: number;
  amount_max?: number;
}

export interface RemittanceCreate {
  aggregator: RemittanceAggregator;
  remittance_date: string;
  amount: string;
  method: RemittanceMethod;
  observation?: string;
  agency_id?: number;
  justification?: File | null;
}

export interface RemittanceMutationResponse {
  success: boolean;
  message: string;
  remittance?: Remittance;
}

export interface RemittanceOptions {
  role: 'ADMIN' | 'GLOBAL_CASH_TRACKING' | 'CASH_TRACKING' | 'AGENCY_HEAD';
  can_filter_by_agency: boolean;
  agencies: { id: number; name: string }[];
  user_agency_id?: number | null;
  user_agency_name?: string | null;
  aggregators: RemittanceAggregator[];
  channels: RemittanceChannel[];
  methods: RemittanceMethod[];
  currency_code: string;
  tracking_start_date?: string | null;
  late_days: number;
  critical_days: number;
  max_file_size_mb: number;
}

export interface TrackingPosition {
  collected_amount: number;
  collected_count: number;
  remitted_amount: number;
  remitted_count: number;
  period_gap: number;
  period_rate: number | null;
  cumulative_collected: number;
  cumulative_remitted: number;
  balance: number;
  cumulative_rate: number | null;
  overdue_amount: number;
  critical_amount: number;
  oldest_unremitted_date?: string | null;
  age_days?: number | null;
  last_remittance_date?: string | null;
  pending_amount: number;
  pending_count: number;
  status: RemittanceTrackingStatus;
}

export interface TrackingRow extends TrackingPosition {
  agency_id: number;
  agency_name?: string | null;
  aggregator: RemittanceAggregator;
}

export interface RemittanceTracking {
  generated_at: string;
  currency_code: string;
  date_from: string;
  date_to: string;
  tracking_start_date?: string | null;
  late_days: number;
  critical_days: number;
  granularity: RemittanceGranularity;
  totals: TrackingPosition;
  aggregators: (TrackingPosition & { aggregator: RemittanceAggregator })[];
  rows: TrackingRow[];
  channels: {
    channel: RemittanceChannel;
    aggregator: RemittanceAggregator;
    amount: number;
    count: number;
  }[];
  document_types: {
    aggregator: RemittanceAggregator;
    document_type: string;
    amount: number;
    count: number;
  }[];
  remittance_methods: { method: RemittanceMethod; amount: number; count: number }[];
  evolution: {
    label: string;
    period_start: string;
    period_end: string;
    collected_amount: number;
    remitted_amount: number;
    balance: number;
  }[];
}

export interface TrackingParams {
  date_from?: string;
  date_to?: string;
  agency_ids?: number[];
  aggregators?: RemittanceAggregator[];
  granularity?: RemittanceGranularity;
}

export interface ElectronicTransaction {
  payment_id: number;
  reference: string;
  payment_date: string;
  amount: number;
  status: 'SUCCESS' | 'PENDING' | 'FAILED';
  aggregator: RemittanceAggregator;
  channel: RemittanceChannel;
  agency_id?: number | null;
  agency_name?: string | null;
  collected_by_name?: string | null;
  phone_number?: string | null;
  external_reference?: string | null;
  invoice_id?: number | null;
  invoice_reference?: string | null;
  invoice_type?: string | null;
  document_reference?: string | null;
}

export interface ElectronicTransactionList {
  items: ElectronicTransaction[];
  total: number;
  page: number;
  size: number;
  pages: number;
  total_amount: number;
  currency_code: string;
}

export interface TransactionParams extends TrackingParams {
  channels?: RemittanceChannel[];
  document_types?: string[];
  statuses?: string[];
  search?: string;
}

export interface RemittanceReminderItem {
  agency_id: number;
  agency_name?: string | null;
  aggregator: RemittanceAggregator;
  balance: number;
  overdue_amount: number;
  critical_amount: number;
  oldest_unremitted_date: string;
  age_days: number;
  last_remittance_date?: string | null;
  status: 'LATE' | 'CRITICAL';
}

export interface RemittanceReminders {
  generated_at: string;
  currency_code: string;
  late_days: number;
  critical_days: number;
  count: number;
  critical_count: number;
  total_balance: number;
  total_overdue: number;
  max_age_days: number;
  items: RemittanceReminderItem[];
}

// ── Service ──────────────────────────────────────────────────────────

// FastAPI expects repeated query params for lists (agency_ids=1&agency_ids=2).
const paramsSerializer = { indexes: null } as const;

/** Drop empty values so they never reach the query string. */
function clean<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        !(Array.isArray(value) && value.length === 0),
    ),
  ) as Partial<T>;
}

/** The proof of remittance, fetched with the session (the route is authenticated). */
async function fetchJustification(remittance: Remittance, download: boolean): Promise<Blob> {
  const response = await apiClient.get<Blob>(`/remittances/${remittance.id}/justification`, {
    params: download ? { download: true } : undefined,
    responseType: 'blob',
  });
  return new Blob([response.data], {
    type: remittance.justification_mime_type || response.data.type || 'application/octet-stream',
  });
}

export const remittancesService = {
  async getOptions(): Promise<RemittanceOptions> {
    const response = await apiClient.get<RemittanceOptions>('/remittances/options');
    return response.data;
  },

  async getRemittances(
    page = 1,
    size = 10,
    filters: RemittanceFilters = {},
  ): Promise<RemittanceListResponse> {
    const response = await apiClient.get<RemittanceListResponse>('/remittances', {
      params: clean({ page, size, ...filters }),
      paramsSerializer,
    });
    return response.data;
  },

  /** Excel export of the list with the same filters (ADMIN only, enforced by the API). */
  async exportRemittances(filters: RemittanceFilters = {}): Promise<Blob> {
    const response = await apiClient.get<Blob>('/remittances/export', {
      params: clean(filters),
      paramsSerializer,
      responseType: 'blob',
    });
    return response.data;
  },

  async getRemittance(id: number): Promise<Remittance> {
    const response = await apiClient.get<Remittance>(`/remittances/${id}`);
    return response.data;
  },

  async createRemittance(data: RemittanceCreate): Promise<RemittanceMutationResponse> {
    const formData = new FormData();
    formData.append('aggregator', data.aggregator);
    formData.append('remittance_date', data.remittance_date);
    formData.append('amount', data.amount);
    formData.append('method', data.method);
    if (data.observation) {formData.append('observation', data.observation);}
    if (data.agency_id !== undefined) {formData.append('agency_id', String(data.agency_id));}
    if (data.justification) {formData.append('justification', data.justification);}
    const response = await apiClient.post<RemittanceMutationResponse>('/remittances', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async cancelRemittance(id: number, reason: string): Promise<RemittanceMutationResponse> {
    const response = await apiClient.post<RemittanceMutationResponse>(
      `/remittances/${id}/cancel`,
      { reason },
    );
    return response.data;
  },

  /** Opens the proof of remittance in a new tab (authenticated download). */
  async openJustification(remittance: Remittance): Promise<void> {
    const url = window.URL.createObjectURL(await fetchJustification(remittance, false));
    // No 'noopener' feature: window.open would then always return null.
    const opened = window.open(url, '_blank');
    if (opened) {
      opened.opener = null;
    } else {
      const link = document.createElement('a');
      link.href = url;
      link.download = remittance.justification_file_name || `${remittance.reference}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  },

  /** Saves the proof of remittance under its original file name. */
  async downloadJustification(remittance: Remittance): Promise<void> {
    const url = window.URL.createObjectURL(await fetchJustification(remittance, true));
    const link = document.createElement('a');
    link.href = url;
    link.download = remittance.justification_file_name || `justificatif_${remittance.reference}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  },

  async getTracking(params: TrackingParams): Promise<RemittanceTracking> {
    const response = await apiClient.get<RemittanceTracking>('/remittances/tracking', {
      params: clean(params),
      paramsSerializer,
    });
    return response.data;
  },

  async getTransactions(
    page = 1,
    size = 10,
    params: TransactionParams = {},
  ): Promise<ElectronicTransactionList> {
    const response = await apiClient.get<ElectronicTransactionList>(
      '/remittances/tracking/transactions',
      { params: clean({ page, size, ...params }), paramsSerializer },
    );
    return response.data;
  },

  async getReminders(): Promise<RemittanceReminders> {
    const response = await apiClient.get<RemittanceReminders>('/remittances/reminders');
    return response.data;
  },
};

export default remittancesService;
